import React from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import Autocomplete from "react-autocomplete";
import classNames from "classnames";
import debounce from "lodash.debounce";
import { injectIntl } from "react-intl";

import { CourseConsumer } from "../../../data/course";

import { Nav } from "./Nav";
import { Title } from "./Title";
import { ValidationErrors } from "./ValidationErrors";
import { SubmitButton } from "./SubmitButton";
import { Label } from "./Label";
import { msg } from "../translations/components/Message";
import messages from "./messages";
import { EducationLevelSelect } from "./EducationLevelSelect";
import {
    EDUCATION_LEVEL_ELEMENTARY,
    EDUCATION_LEVEL_SECONDARY,
    EDUCATION_LEVEL_JUNIOR_HIGH,
    EDUCATION_LEVEL_JUNIOR_MIDDLE,
    EDUCATION_LEVEL_HIGH_SCHOOL,
    TYPE_ADULT
} from "./constants";
import { TopNav } from "./TopNav";
import { getLocale } from "../../../messages";

const locale = getLocale() || "nl";
const other = { id: 136, name: locale?.includes("fr") ? "Autre" : "Ander" };

const getOtherName = (courses, otherCourseName) => {
    return courses.find(c => c.id === other.id) ? otherCourseName : "";
};

class SelectCourse extends React.PureComponent {
    constructor(props) {
        super(props);

        // initial, local state, using any values if they exist
        this.state = {
            courses: props.courses,
            educationLevel: props.educationLevel,
            otherCourseName: props.otherCourseName,
            text: props.text,
            year: props.year,
            isNl: props.isNl === "True"
        };
    }

    onSubmit = event => {
        if (event) {
            event.preventDefault();
        }
        const { otherCourseName, ...state } = this.state;
        const { onSubmit } = this.props;
        const otherName = getOtherName(state.courses, otherCourseName);

        if (!this.nextEnabled()) {
            return;
        }

        onSubmit({
            ...state,
            otherCourseName: otherName
        });
    };

    onPrev = () => {
        const { otherCourseName, ...state } = this.state;
        const otherName = getOtherName(state.courses, otherCourseName);
        this.props.prevStep({
            [SelectCourse.STEP_KEY]: {
                ...state,
                otherCourseName: otherName
            }
        });
    };

    onCourseSelect = course => {
        this.setState(state => ({ courses: [...state.courses, course] }));
    };

    onCourseCancel = course => {
        this.setState(state => ({
            courses: state.courses.filter(c => c.id !== course.id)
        }));
    };

    onEducationLevelSelect = educationLevel => {
        this.setState({ educationLevel: educationLevel.value });
    };

    onChange = event => {
        const { name, value } = event.target;

        this.setState({ [name]: value });
    };

    nextEnabled = () => {
        const { courses, educationLevel, year } = this.state;
        const yearOptions = this.getYearOptions();
        const yearRequired = yearOptions.length > 0;
        const yearFilled = year && !isNaN(year) && parseInt(year) > 0;
        const basicRequirementsMet = courses.length > 0 && educationLevel;

        return yearRequired
            ? basicRequirementsMet && yearFilled
            : basicRequirementsMet;
    };
    setOtherCourseName = e => {
        this.setState({ otherCourseName: e.target.value });
    };


    getYearLabel = () => {
        const { educationLevel } = this.state;
        if (educationLevel === EDUCATION_LEVEL_ELEMENTARY)
            return msg(messages.yearElementary);
        return msg(messages.year);
    }

    getYearOptions = () => {
        const { isNl, educationLevel } = this.state;

        if (!educationLevel) return [];

        const languageCode = isNl ? 'nl-nl' : 'default';

        // Create year options once (or move this to a constant outside the method)
        const allYears = Array.from({ length: 8 }, (_, i) => ({
            value: String(i + 1),
            label: String(i + 1)
        }));

        // Define year choices based on education level and locale
        const yearChoices = {
            "nl-nl": {
                [EDUCATION_LEVEL_ELEMENTARY]: allYears,
                [EDUCATION_LEVEL_JUNIOR_HIGH]: allYears.slice(0, 4),
                [EDUCATION_LEVEL_JUNIOR_MIDDLE]: allYears.slice(0, 5),
                [EDUCATION_LEVEL_HIGH_SCHOOL]: allYears.slice(0, 6)
            },
            "default": {
                [EDUCATION_LEVEL_ELEMENTARY]: allYears.slice(0, 6),
                [EDUCATION_LEVEL_SECONDARY]: allYears.slice(0, 7),
                [EDUCATION_LEVEL_JUNIOR_HIGH]: allYears.slice(0, 2),
                [EDUCATION_LEVEL_JUNIOR_MIDDLE]: allYears.slice(2, 4),
                [EDUCATION_LEVEL_HIGH_SCHOOL]: allYears.slice(4, 6),
            }
        };

        // Get the appropriate year choices with fallback to empty array
        return yearChoices[languageCode]?.[educationLevel] ?? [];
    };

    render() {
        const {
            courses,
            educationLevel,
            otherCourseName,
            text,
            year
        } = this.state;
        const { validationErrors, studentType, onClose, focus } = this.props;
        const yearOptions = this.getYearOptions();
        const yearLabel = this.getYearLabel();
        return (
            <div>
                <TopNav onClose={onClose} focus={focus} onPrev={this.onPrev} />
                <Title>{msg(messages.guidanceTitle)}</Title>
                <form
                    className="interactive-signup__form"
                    onSubmit={this.onSubmit.bind(this)}
                >
                    <Label>{msg(messages.topic)}*:</Label>
                    <TranslatedCourseAutoComplete
                        onCourseSelect={this.onCourseSelect}
                        onCourseCancel={this.onCourseCancel}
                        courses={courses}
                        studentType={studentType}
                        otherCourseName={otherCourseName}
                        setOtherCourseName={this.setOtherCourseName}
                    />
                    <ValidationErrors errors={validationErrors.courses} />

                    <Label>{msg(messages.level)}*:</Label>
                    <EducationLevelSelect
                        onSelect={this.onEducationLevelSelect}
                        studentType={studentType}
                        value={educationLevel}
                        isDisabled={studentType === TYPE_ADULT}
                    />

                    {!!yearOptions.length && (
                        <>
                            <Label>{yearLabel}:</Label>
                            <Select
                                className="bh-select__container"
                                classNamePrefix="bh-select"
                                isSearchable={false}
                                value={{ value: year, label: year }}
                                options={this.getYearOptions()}
                                onChange={option => {
                                    this.setState({ year: option.value });
                                }}
                            />
                            <ValidationErrors errors={validationErrors.year} />
                        </>
                    )}

                    <Label>{msg(messages.moreInfo)}</Label>
                    <textarea
                        className="interactive-signup-textarea"
                        name={"text"}
                        onChange={this.onChange}
                        value={text}
                    />

                    <SubmitButton />
                </form>
                <div className="interactive-signup__button-row">
                    <Nav onClick={this.onPrev} direction="prev" />
                    <Nav
                        onClick={this.onSubmit.bind(this)}
                        direction="next"
                        disabled={!this.nextEnabled()}
                    />
                </div>
            </div>
        );
    }
}

SelectCourse.STEP_KEY = "course";

SelectCourse.propTypes = {
    onSubmit: PropTypes.func.isRequired,
    nextStep: PropTypes.func.isRequired,
    prevStep: PropTypes.func.isRequired,
    studentType: PropTypes.string,
    courses: PropTypes.arrayOf(PropTypes.object),
    educationLevel: PropTypes.string,
    validationErrors: PropTypes.object,
    year: PropTypes.string
};

SelectCourse.defaultProps = {
    courses: [],
    educationLevel: "",
    validationErrors: {},
    otherCourseName: "",
    year: null
};

export default injectIntl(SelectCourse);

class CourseAutoComplete extends React.Component {
    constructor(props) {
        super(props);

        this.courseConsumer = new CourseConsumer();

        this.state = {
            value: "",
            items: []
        };
    }

    debouncedSearch = debounce(input => {
        const { courses, studentType } = this.props;
        const usedCourses = courses.map(course => course.id);
        const courseType =
            studentType === TYPE_ADULT ? "student_professional" : undefined;

        return this.courseConsumer.search(input, courseType).then(items => {
            if (!items.length && !usedCourses.includes(other.id)) {
                items.push(other);
            }
            this.setState({
                items: items.filter(course => !usedCourses.includes(course.id))
            });
        });
    }, 300);

    getOptions(event, input) {
        if (!input) {
            return this.setState({
                items: [],
                value: ""
            });
        }
        this.setState({ value: input });
        this.debouncedSearch(input);
    }

    onSelect(value, course) {
        const { onCourseSelect } = this.props;
        let { items } = this.state;

        // set the menu to remaining valid options
        this.setState(
            { value: "", items: items.filter(item => item.id !== course.id) },
            () => onCourseSelect(course)
        );
    }

    render() {
        const { value, items } = this.state;
        const {
            courses,
            onCourseCancel,
            intl,
            otherCourseName,
            setOtherCourseName
        } = this.props;

        return (
            <div className="interactive-signup__autocomplete-wrapper">
                <Autocomplete
                    inputProps={{
                        className:
                            "interactive-signup__input interactive-signup__input--first",
                        placeholder: intl.formatMessage(
                            messages.coursePlaceholder
                        ),
                        autoFocus: true
                    }}
                    // pass down the input text
                    value={value}
                    // show the matching items in the menu
                    items={items}
                    // course-to-input-value (=display the name)
                    getItemValue={course => course.name}
                    // when an option is selected, update the state and call the
                    // callback to update the global state
                    onSelect={this.onSelect.bind(this)}
                    // if the typed input-value changes, re-fetch the possible options from the backend
                    onChange={this.getOptions.bind(this)}
                    // control how the menu looks - mostly applying our own CSS class
                    renderMenu={(items, value) => {
                        if (items.length === 0) {
                            let style = {};
                            if (!value) {
                                style.display = "none";
                            }
                            return (
                                <div
                                    className="select-course-menu"
                                    style={style}
                                >
                                    {msg(messages.noResults)}
                                </div>
                            );
                        } else {
                            return (
                                <div className="select-course-menu">
                                    {items}
                                </div>
                            );
                        }
                    }}
                    // how a single item looks in the options menu
                    renderItem={(course, isHighlighted) => (
                        <div key={course.id}>
                            <SelectCourseMenuItem
                                course={course}
                                isHighlighted={isHighlighted}
                                searchString={value}
                            />
                        </div>
                    )}
                />

                <div className="interactive-signup__selected-courses">
                    {courses.map(course => (
                        <SelectedCourse
                            intl={intl}
                            key={course.id}
                            course={course}
                            onCancel={onCourseCancel}
                            otherCourseName={otherCourseName}
                            setOtherCourseName={setOtherCourseName}
                        />
                    ))}
                </div>
            </div>
        );
    }
}

CourseAutoComplete.propTypes = {
    onCourseSelect: PropTypes.func.isRequired,
    onCourseCancel: PropTypes.func.isRequired,
    courses: PropTypes.arrayOf(PropTypes.object),
    studentType: PropTypes.string
};

CourseAutoComplete.defaultProps = {
    courses: []
};

const TranslatedCourseAutoComplete = injectIntl(CourseAutoComplete);

const SelectedCourse = ({
    course,
    onCancel,
    otherCourseName,
    setOtherCourseName,
    intl
}) => {
    return (
        <div className="interactive-signup__selected-course-wrapper">
            <div
                key={course.id}
                className="interactive-signup__selected-course"
            >
                {course.name}
                <i
                    className="fa fa-times interactive-signup__selected-course-cancel"
                    onClick={() => onCancel(course)}
                />
            </div>
            {course.id === other.id && (
                <input
                    className="interactive-signup__input interactive-signup__input--small"
                    name="other"
                    value={otherCourseName}
                    onChange={setOtherCourseName}
                    placeholder={intl.formatMessage(messages.otherPlaceholder)}
                />
            )}
        </div>
    );
};

SelectedCourse.propTypes = {
    course: PropTypes.object.isRequired,
    onCancel: PropTypes.func.isRequired
};

// a text part that may or may not be highlighted with the search value
// FIXME: sometimes there's a weird spacing inside of the span -> investigate
const Highlight = props => {
    const className = classNames("select-course-menu__item-bit", {
        "select-course-menu__item-bit--highlight": props.highlight
    });
    return <span className={className}>{props.children}</span>;
};

Highlight.propTypes = {
    highlight: PropTypes.bool
};

Highlight.defaultProps = {
    highlight: false
};

// given a search string and the original course name, mark the matching
// search parts so they can be highlighted with CSS
const highLightMatches = (searchString, courseName) => {
    if (!searchString) {
        return courseName;
    }

    const original = courseName;
    const indices = [];

    searchString = searchString.toLowerCase();
    courseName = courseName.toLowerCase();

    for (let i = 0; i < courseName.length; ++i) {
        if (courseName.substring(i, i + searchString.length) === searchString) {
            indices.push(i);
        }
    }

    if (!indices.length) {
        return original;
    }

    let bits = [];

    if (indices[0] !== 0) {
        bits.push(
            <Highlight highlight={false} key="miss-0">
                {original.substring(0, indices[0])}
            </Highlight>
        );
    }

    for (let matchIndex of indices) {
        const i = indices.indexOf(matchIndex);
        const text = original.substring(
            matchIndex,
            matchIndex + searchString.length
        );
        bits.push(
            <Highlight highlight={true} key={`hit-${i}`}>
                {text}
            </Highlight>
        );

        // add the reminder as regular string
        let nextMatchIndex = indices[i + 1];
        if (nextMatchIndex) {
            const remainder = original.substring(
                matchIndex + searchString.length,
                nextMatchIndex
            );
            if (remainder) {
                bits.push(
                    <Highlight highlight={false} key={`miss-${i + 1}`}>
                        {" "}
                        {remainder}{" "}
                    </Highlight>
                );
            }
        }
    }

    // do we need to add the end?
    const lastIndex = indices[indices.length - 1];
    const lastMiss = original.substring(lastIndex + searchString.length);
    if (lastMiss) {
        bits.push(
            <Highlight
                highlight={false}
                key={`miss-${indices.indexOf(lastIndex) + 1}`}
            >
                {lastMiss}
            </Highlight>
        );
    }

    return bits;
};

// render a single course in the menu as a highlighted item
const SelectCourseMenuItem = props => {
    const { course, isHighlighted, searchString } = props;
    const highlighted = highLightMatches(searchString, course.name);
    const className = classNames("select-course-menu__item", {
        "select-course-menu__item--highlight": isHighlighted
    });
    return <div className={className}> {highlighted} </div>;
};

SelectCourseMenuItem.propTypes = {
    searchString: PropTypes.string.isRequired,
    course: PropTypes.object.isRequired,
    isHighlighted: PropTypes.bool
};
